import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheoryProgress } from '../../hooks/useTheoryProgress';
import { useVideoProgress } from '../../hooks/useVideoProgress';

const BigONotation: React.FC = () => {
  const navigate = useNavigate();
  useTheoryProgress('bigO');
  const videoRef = useVideoProgress('bigO');
  const [activeSection, setActiveSection] = React.useState<string | null>(null);

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const handleBackClick = () => {
    navigate('/topics');
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>
        
        {/* Title and description */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Big-O Notation</h1>
          <p className="text-white/70 text-lg">Learn how to measure and compare algorithm efficiency using Big-O notation.</p>
        </motion.div>

        <div className="space-y-12">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-invert max-w-none"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">What Is Big-O Notation?</h2>
            <div className="text-white/70 space-y-4">
              <p>
                Big-O notation is like a speedometer for algorithms. It tells us how fast an algorithm's runtime grows as the input size increases.
              </p>
              <p>
                Think of it like this: If you're driving a car, Big-O notation tells you whether your fuel efficiency stays the same (constant time) or gets worse as you drive further (linear or worse).
              </p>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Key Concepts</h3>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  <li><strong>Time Complexity:</strong> How fast an algorithm runs</li>
                  <li><strong>Space Complexity:</strong> How much memory an algorithm uses</li>
                  <li><strong>Worst-Case Analysis:</strong> How the algorithm performs in the worst scenario</li>
                  <li><strong>Asymptotic Behavior:</strong> Focus on performance as input size approaches infinity</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Common Types */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-6">Common Big-O Complexities</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* O(1) */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('o1')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">O(1) - Constant Time</h3>
                  <div className="text-2xl">⚡</div>
                </div>
                <p className="text-white/70 mb-3">
                  The fastest possible. Takes the same time no matter how big the input is.
                </p>
                <ul className="list-disc list-inside text-white/60 space-y-1">
                  <li>Instant execution</li>
                  <li>No loops needed</li>
                  <li>Example: Getting the first item in an array</li>
                </ul>
                
                {activeSection === 'o1' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// O(1) - Constant Time Example
function getFirstElement(array) {
  return array[0]; // Always takes the same time
}

// Usage
const numbers = [1, 2, 3, 4, 5];
const first = getFirstElement(numbers); // O(1)`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* O(n) */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('on')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">O(n) - Linear Time</h3>
                  <div className="text-2xl">📊</div>
                </div>
                <p className="text-white/70 mb-3">
                  Time grows directly with input size. One operation per item.
                </p>
                <ul className="list-disc list-inside text-white/60 space-y-1">
                  <li>Single loop through the input</li>
                  <li>Common in basic searches</li>
                  <li>Example: Finding a name in a phone book</li>
                </ul>
                
                {activeSection === 'on' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// O(n) - Linear Time Example
function findElement(array, target) {
  for (let i = 0; i < array.length; i++) {
    if (array[i] === target) return i;
  }
  return -1;
}

// Usage
const numbers = [1, 2, 3, 4, 5];
const index = findElement(numbers, 3); // O(n)`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* O(n²) */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('on2')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">O(n²) - Quadratic Time</h3>
                  <div className="text-2xl">📉</div>
                </div>
                <p className="text-white/70 mb-3">
                  Gets slow quickly. Time grows with the square of input size.
                </p>
                <ul className="list-disc list-inside text-white/60 space-y-1">
                  <li>Nested loops</li>
                  <li>Common in basic sorting</li>
                  <li>Example: Checking all pairs in a list</li>
                </ul>
                
                {activeSection === 'on2' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// O(n²) - Quadratic Time Example
function findPairs(array) {
  for (let i = 0; i < array.length; i++) {
    for (let j = 0; j < array.length; j++) {
      console.log(\`\${array[i]}, \${array[j]}\`);
    }
  }
}

// Usage
const numbers = [1, 2, 3];
findPairs(numbers); // O(n²)`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
              
              {/* O(log n) */}
              <motion.div 
                className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => toggleSection('ologn')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">O(log n) - Logarithmic Time</h3>
                  <div className="text-2xl">📈</div>
                </div>
                <p className="text-white/70 mb-3">
                  Very efficient. Problem size halves each step.
                </p>
                <ul className="list-disc list-inside text-white/60 space-y-1">
                  <li>Great for large inputs</li>
                  <li>Common in searching</li>
                  <li>Example: Finding a word in a dictionary</li>
                </ul>
                
                {activeSection === 'ologn' && (
                  <motion.div 
                    className="mt-4 pt-4 border-t border-white/10"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-white/5 p-4 rounded-lg mb-3">
                      <pre className="text-sm text-white/80 font-mono">
                        <code>{`// O(log n) - Logarithmic Time Example
function binarySearch(array, target) {
  let left = 0;
  let right = array.length - 1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (array[mid] === target) return mid;
    if (array[mid] < target) left = mid + 1;
    else right = mid - 1;
  }
  return -1;
}

// Usage
const sortedNumbers = [1, 2, 3, 4, 5];
const index = binarySearch(sortedNumbers, 3); // O(log n)`}</code>
                      </pre>
                    </div>
                    <div className="flex justify-end">
                      <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Learn more →
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.section>

          {/* Time Complexity Comparison */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Time Complexity Comparison</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                How different complexities perform with increasing input sizes (n):
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-white/70">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left">n</th>
                      <th className="py-3 px-4 text-left">O(1)</th>
                      <th className="py-3 px-4 text-left">O(log n)</th>
                      <th className="py-3 px-4 text-left">O(n)</th>
                      <th className="py-3 px-4 text-left">O(n²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">10</td>
                      <td className="py-3 px-4">1</td>
                      <td className="py-3 px-4">~3</td>
                      <td className="py-3 px-4">10</td>
                      <td className="py-3 px-4">100</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-3 px-4">100</td>
                      <td className="py-3 px-4">1</td>
                      <td className="py-3 px-4">~7</td>
                      <td className="py-3 px-4">100</td>
                      <td className="py-3 px-4">10,000</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">1,000</td>
                      <td className="py-3 px-4">1</td>
                      <td className="py-3 px-4">~10</td>
                      <td className="py-3 px-4">1,000</td>
                      <td className="py-3 px-4">1,000,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Why Is It Important? */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Why Is Big-O Notation Important?</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Choose Better Algorithms:</strong> Helps pick the fastest solution for your problem.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Write Faster Code:</strong> Find and fix slow parts of your program.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Handle Big Data:</strong> Make sure your code works well with large amounts of data.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">✓</span>
                  <span><strong>Predict Performance:</strong> Estimate how your code will scale with more users or data.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Real-world Applications */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Real-world Applications</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Web Search</h3>
                <p className="text-white/70">
                  Search engines use O(log n) algorithms to find results quickly in billions of web pages.
                </p>
              </div>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Social Media</h3>
                <p className="text-white/70">
                  O(n) algorithms help show your friends' posts in your feed efficiently.
                </p>
              </div>
              <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Online Shopping</h3>
                <p className="text-white/70">
                  O(1) operations make adding items to your cart instant, no matter how many items are in the store.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Video Tutorial */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/5 rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Video Tutorial</h2>
            <div className="aspect-w-16 aspect-h-9 bg-black/50 rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                >
                  <source src="https://files.catbox.moe/merp80.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
            </div>
          </motion.section>

          {/* Next Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <h2 className="text-2xl font-semibold text-white mb-4">Next Steps</h2>
            <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
              <p className="text-white/70 mb-4">
                Now that you understand the basics of Big-O notation, you can:
              </p>
              <ul className="list-disc list-inside text-white/70 space-y-2">
                <li>Practice analyzing the time complexity of simple algorithms</li>
                <li>Learn about space complexity in more depth</li>
                <li>Explore advanced complexities like O(n log n) and O(2^n)</li>
                <li>Apply Big-O analysis to optimize your own code</li>
              </ul>
              <div className="mt-6 flex justify-center">
                <motion.button
                  onClick={() => navigate('/quiz/big-o')}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span>Take Quiz</span>
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default BigONotation;